import React from 'react';
import {
  createEmotionCache, keyframes, MantineProvider, MantineThemeOverride, useMantineColorScheme,
} from '@mantine/core';

import { PROJECT_SLUG } from './constants';

const interSemiBold = 600;

const t: MantineThemeOverride = {
  defaultRadius: 'md',
  colors: {
    brand: ['#ECEEF4', '#D7DBEA', '#C0C9E4', '#A6B5E2', '#8AA0E4', '#6A8AED', '#4371FD', '#426BE8', '#4165D3', '#3070FE'],
  },
  black: '#495057', // theme.colors.gray[7]
  primaryColor: 'brand',
  fontFamily: 'Inter, sans-serif',
  fontSizes: {
    xs: 12.5,
  },
  headings: {
    fontFamily: 'Inter, sans-serif',
    fontWeight: interSemiBold,
  },
  other: {
    lightBodyBackgroundColor: '#fafcfc',
    formSectionSeperatorBackgroundColor: '#f8f9fa',
    formSectionSeperatorDarkBackgroundColor: '#1b2024',
    chartColors: [
      '#5065e7', '#ed8d5d', '#dd4b33', '#1c2e39', '#73bac7',
      '#88d498', '#ea4971', '#f5c542', '#8e7cc3', '#6fa3ef',
      '#d5a6bd', '#42a5f5', '#ab9353', '#a8a495', '#78866b',
    ],
  },
  components: {
    Anchor: {
      defaultProps: {
        color: 'brand.6',
      },
    },
    Modal: {
      styles: (theme) => ({
        header: {
          fontSize: theme.fontSizes.md,
          fontWeight: interSemiBold,
        },
      }),
    },
    Tabs: {
      styles: () => ({
        tabsList: {
          flexWrap: 'nowrap',
          borderBottomWidth: 1,
          '& > button': {
            borderBottomWidth: 1,
            marginBottom: -1,
          },
        },
      }),
    },
    Tooltip: {
      defaultProps: {
        transition: 'pop',
        withArrow: true,
      },
    },
    Indicator: {
      styles: (theme, params) => {
        const c = theme.fn.variant({
          variant: 'filled',
          primaryFallback: false,
          color: params.color || theme.primaryColor,
        }).background as string;

        const cmode = theme.colorScheme === 'dark' ? theme.fn.darken : theme.fn.lighten;
        const calpha = theme.colorScheme === 'dark' ? 0.1 : 0.5;

        return {
          indicator: {
            zIndex: 99,
          },
          processing: {
            zIndex: 99,
            animation: `${keyframes({
              '0%': {
                boxShadow: `0 0 0 0 ${cmode(c, calpha)}`,
              },
              '35%': {
                boxShadow: '0 0 0 10px transparent',
              },
              '50%': {
                boxShadow: '0 0 0 0 transparent',
              },
              '100%': {
                boxShadow: '0 0 0 0 transparent',
              },
            })} 3000ms linear infinite`,
          },
        };
      },
    },
  },
  globalStyles: (theme) => ({
    body: {
      backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.other.lightBodyBackgroundColor,
      color: theme.colorScheme === 'dark' ? theme.colors.dark[0] : theme.colors.gray[7],
      fontSize: theme.fontSizes.sm,
    },
  }),
};

const ec = createEmotionCache({ key: PROJECT_SLUG });

export const ThemeProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { colorScheme } = useMantineColorScheme();
  t.colorScheme = colorScheme;

  return (
    <MantineProvider withGlobalStyles withNormalizeCSS theme={t} emotionCache={ec}>
      {children}
    </MantineProvider>
  );
};
